import React, { useState, useEffect } from "react";
import { Container, Card, Button, Table, Modal, Form, Row, Col, Badge, Alert, Spinner } from "react-bootstrap";
import { FaCalendarAlt, FaPlus, FaEdit, FaTrash, FaFilter } from "react-icons/fa";
import { collection, query, where, getDocs, doc, setDoc, deleteDoc, getFirestore } from "firebase/firestore";
import firebaseApp from "../../firebase/credenciales";
import Swal from "sweetalert2";
import { format } from "date-fns";

const db = getFirestore(firebaseApp);

const GestionFeriados = ({ usuario }) => {
    const [feriados, setFeriados] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [modoEdicion, setModoEdicion] = useState(false);
    const [feriadoSeleccionado, setFeriadoSeleccionado] = useState(null);
    const [generandoFeriados, setGenerandoFeriados] = useState(false);
    const [coleccionVacia, setColeccionVacia] = useState(false);

    // Filtros
    const [filtroAño, setFiltroAño] = useState(new Date().getFullYear());
    const [filtroTipo, setFiltroTipo] = useState("");

    // Formulario
    const [formData, setFormData] = useState({
        fecha: "",
        nombre: "",
        tipo: "nacional",
        region: "",
        activo: true
    });

    // Lista de feriados nacionales de Perú (16 feriados oficiales)
    const feriadosNacionalesTemplate = [
        { mes: 1, dia: 1, nombre: "Año Nuevo" },
        { mes: 5, dia: 1, nombre: "Día del Trabajo" },
        { mes: 6, dia: 29, nombre: "San Pedro y San Pablo" },
        { mes: 7, dia: 28, nombre: "Fiestas Patrias - Día de la Independencia" },
        { mes: 7, dia: 29, nombre: "Fiestas Patrias - Día de las Glorias del Ejército" },
        { mes: 8, dia: 6, nombre: "Batalla de Junín" },
        { mes: 8, dia: 30, nombre: "Santa Rosa de Lima" },
        { mes: 10, dia: 8, nombre: "Combate de Angamos" },
        { mes: 11, dia: 1, nombre: "Todos los Santos" },
        { mes: 12, dia: 8, nombre: "Inmaculada Concepción" },
        { mes: 12, dia: 9, nombre: "Batalla de Ayacucho" },
        { mes: 12, dia: 25, nombre: "Navidad" },
        { mes: 12, dia: 31, nombre: "Año Nuevo (desde mediodía)" },
    ];

    // Feriados móviles (Semana Santa) por año
    const semanaSantaPorAño = {
        2024: [
            { mes: 3, dia: 28, nombre: "Jueves Santo" },
            { mes: 3, dia: 29, nombre: "Viernes Santo" },
            { mes: 3, dia: 30, nombre: "Sábado de Gloria" },
        ],
        2025: [
            { mes: 4, dia: 17, nombre: "Jueves Santo" },
            { mes: 4, dia: 18, nombre: "Viernes Santo" },
            { mes: 4, dia: 19, nombre: "Sábado de Gloria" },
        ],
        2026: [
            { mes: 4, dia: 2, nombre: "Jueves Santo" },
            { mes: 4, dia: 3, nombre: "Viernes Santo" },
            { mes: 4, dia: 4, nombre: "Sábado de Gloria" },
        ],
        2027: [
            { mes: 3, dia: 25, nombre: "Jueves Santo" },
            { mes: 3, dia: 26, nombre: "Viernes Santo" },
            { mes: 3, dia: 27, nombre: "Sábado de Gloria" },
        ],
    };

    useEffect(() => {
        cargarFeriados();
        verificarColeccionVacia();
    }, [filtroAño, filtroTipo]);

    const verificarColeccionVacia = async () => {
        try {
            // Verificar si existe algún feriado en toda la colección (sin filtro de año)
            const q = query(collection(db, `pl_feriados/${usuario.ruc}/feriados`));
            const snapshot = await getDocs(q);
            setColeccionVacia(snapshot.empty);
        } catch (error) {
            console.error("Error verificando colección:", error);
        }
    };

    const cargarFeriados = async () => {
        try {
            setLoading(true);
            let q = query(
                collection(db, `pl_feriados/${usuario.ruc}/feriados`),
                where("año", "==", filtroAño)
            );

            if (filtroTipo) {
                q = query(q, where("tipo", "==", filtroTipo));
            }

            const snapshot = await getDocs(q);
            const feriadosData = [];
            snapshot.forEach(doc => {
                feriadosData.push({ id: doc.id, ...doc.data() });
            });

            feriadosData.sort((a, b) => a.fecha.localeCompare(b.fecha));
            setFeriados(feriadosData);
        } catch (error) {
            console.error("Error cargando feriados:", error);
            Swal.fire("Error", "No se pudieron cargar los feriados", "error");
        } finally {
            setLoading(false);
        }
    };

    const generarFeriadosNacionales = async () => {
        const result = await Swal.fire({
            title: "¿Generar feriados nacionales?",
            html: `Se crearán automáticamente los <strong>16 feriados nacionales</strong> de Perú para el año <strong>${filtroAño}</strong>.<br><br>
                   <small>Nota: Si ya existen algunos feriados, se actualizarán.</small>`,
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: "#28a745",
            cancelButtonColor: "#6c757d",
            confirmButtonText: "Sí, generar",
            cancelButtonText: "Cancelar"
        });

        if (!result.isConfirmed) return;

        try {
            setGenerandoFeriados(true);

            // Combinar feriados fijos y móviles
            const todosLosFeriados = [...feriadosNacionalesTemplate];

            // Agregar Semana Santa si existe para el año
            if (semanaSantaPorAño[filtroAño]) {
                todosLosFeriados.push(...semanaSantaPorAño[filtroAño]);
            }

            let contador = 0;
            for (const feriado of todosLosFeriados) {
                const docId = `${filtroAño}_${String(feriado.mes).padStart(2, '0')}_${String(feriado.dia).padStart(2, '0')}`;
                const fecha = `${filtroAño}-${String(feriado.mes).padStart(2, '0')}-${String(feriado.dia).padStart(2, '0')}`;

                const feriadoData = {
                    fecha: fecha,
                    nombre: feriado.nombre,
                    tipo: "nacional",
                    region: null,
                    activo: true,
                    año: filtroAño,
                    mes: feriado.mes,
                    dia: feriado.dia,
                    creado_por: usuario.email,
                    fecha_creacion: new Date()
                };

                await setDoc(doc(db, `pl_feriados/${usuario.ruc}/feriados`, docId), feriadoData);
                contador++;
            }

            Swal.fire({
                icon: "success",
                title: "¡Feriados generados!",
                text: `Se crearon ${contador} feriados nacionales para el año ${filtroAño}`,
                timer: 3000
            });

            cargarFeriados();
            verificarColeccionVacia();
        } catch (error) {
            console.error("Error generando feriados:", error);
            Swal.fire("Error", "No se pudieron generar los feriados nacionales", "error");
        } finally {
            setGenerandoFeriados(false);
        }
    };

    const handleNuevo = () => {
        setModoEdicion(false);
        setFeriadoSeleccionado(null);
        setFormData({
            fecha: "",
            nombre: "",
            tipo: "nacional",
            region: "",
            activo: true
        });
        setShowModal(true);
    };

    const handleEditar = (feriado) => {
        setModoEdicion(true);
        setFeriadoSeleccionado(feriado);
        setFormData({
            fecha: feriado.fecha,
            nombre: feriado.nombre,
            tipo: feriado.tipo,
            region: feriado.region || "",
            activo: feriado.activo
        });
        setShowModal(true);
    };

    const handleGuardar = async () => {
        try {
            if (!formData.fecha || !formData.nombre) {
                Swal.fire("Error", "Complete todos los campos obligatorios", "warning");
                return;
            }

            const [año, mes, dia] = formData.fecha.split("-").map(Number);
            const docId = `${año}_${String(mes).padStart(2, '0')}_${String(dia).padStart(2, '0')}`;

            const feriadoData = {
                fecha: formData.fecha,
                nombre: formData.nombre,
                tipo: formData.tipo,
                region: formData.tipo === "regional" ? formData.region : null,
                activo: formData.activo,
                año: año,
                mes: mes,
                dia: dia,
                creado_por: usuario.email,
                fecha_creacion: new Date()
            };

            await setDoc(doc(db, `pl_feriados/${usuario.ruc}/feriados`, docId), feriadoData);

            Swal.fire("Éxito", `Feriado ${modoEdicion ? 'actualizado' : 'creado'} correctamente`, "success");
            setShowModal(false);
            cargarFeriados();
        } catch (error) {
            console.error("Error guardando feriado:", error);
            Swal.fire("Error", "No se pudo guardar el feriado", "error");
        }
    };

    const handleEliminar = async (feriado) => {
        const result = await Swal.fire({
            title: "¿Eliminar feriado?",
            text: `Se eliminará: ${feriado.nombre}`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Sí, eliminar",
            cancelButtonText: "Cancelar"
        });

        if (result.isConfirmed) {
            try {
                await deleteDoc(doc(db, `pl_feriados/${usuario.ruc}/feriados`, feriado.id));
                Swal.fire("Eliminado", "Feriado eliminado correctamente", "success");
                cargarFeriados();
            } catch (error) {
                console.error("Error eliminando feriado:", error);
                Swal.fire("Error", "No se pudo eliminar el feriado", "error");
            }
        }
    };

    const getTipoBadge = (tipo) => {
        switch (tipo) {
            case "nacional": return "primary";
            case "regional": return "info";
            case "empresa": return "warning";
            default: return "secondary";
        }
    };

    const formatearFecha = (fechaStr) => {
        try {
            const [año, mes, dia] = fechaStr.split("-");
            return `${dia}/${mes}/${año}`;
        } catch {
            return fechaStr;
        }
    };

    return (
        <div
            className="min-vh-100"
            style={{
                background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
                minHeight: "100vh",
                width: "100vw",
                maxWidth: "100%",
                padding: "20px 0",
            }}
        >
            <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
                <Card
                    className="shadow-lg border-0"
                    style={{
                        borderRadius: "15px",
                        overflow: "hidden",
                        minHeight: "90vh",
                    }}
                >
                    {/* Header */}
                    <Card.Header
                        className="text-white py-4"
                        style={{
                            background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                            borderBottom: "3px solid #D2691E",
                        }}
                    >
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <h2 className="fw-bold mb-1">
                                    <FaCalendarAlt className="me-3" />
                                    Gestión de Feriados
                                </h2>
                                <p className="mb-0 opacity-75">
                                    Administración de días feriados nacionales, regionales y de empresa
                                </p>
                            </div>
                            <div className="d-flex gap-2">
                                {coleccionVacia && (
                                    <Button
                                        variant="info"
                                        onClick={generarFeriadosNacionales}
                                        disabled={generandoFeriados}
                                        style={{
                                            borderRadius: "10px",
                                            background: "linear-gradient(135deg, #17a2b8 0%, #138496 100%)",
                                            border: "none",
                                            color: "white",
                                            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                                            padding: "10px 20px",
                                        }}
                                    >
                                        {generandoFeriados ? (
                                            <>
                                                <Spinner animation="border" size="sm" className="me-2" />
                                                Generando...
                                            </>
                                        ) : (
                                            <>
                                                <FaCalendarAlt className="me-2" />
                                                Generar Feriados Nacionales
                                            </>
                                        )}
                                    </Button>
                                )}
                                <Button
                                    variant="light"
                                    onClick={handleNuevo}
                                    style={{
                                        borderRadius: "10px",
                                        background: "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                                        border: "none",
                                        color: "white",
                                        boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                                        padding: "10px 20px",
                                    }}
                                >
                                    <FaPlus className="me-2" />
                                    Nuevo Feriado
                                </Button>
                            </div>
                        </div>
                    </Card.Header>

                    <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
                        {/* Filtros */}
                        <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
                            <Card.Header
                                className="text-white py-3"
                                style={{
                                    background: "linear-gradient(135deg, #D2691E 0%, #CD853F 100%)",
                                    borderRadius: "15px 15px 0 0",
                                }}
                            >
                                <h5 className="mb-0 fw-semibold">
                                    <FaFilter className="me-2" />
                                    Filtros
                                </h5>
                            </Card.Header>
                            <Card.Body className="p-4">
                                <Row>
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>Año</Form.Label>
                                            <Form.Select
                                                value={filtroAño}
                                                onChange={(e) => setFiltroAño(parseInt(e.target.value))}
                                            >
                                                {[2024, 2025, 2026, 2027].map(año => (
                                                    <option key={año} value={año}>{año}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>Tipo</Form.Label>
                                            <Form.Select
                                                value={filtroTipo}
                                                onChange={(e) => setFiltroTipo(e.target.value)}
                                            >
                                                <option value="">Todos</option>
                                                <option value="nacional">Nacional</option>
                                                <option value="regional">Regional</option>
                                                <option value="empresa">Empresa</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* Tabla */}
                        <Card className="shadow-sm border-0" style={{ borderRadius: "15px" }}>
                            <Card.Body className="p-4">
                                {loading ? (
                                    <div className="text-center py-5">
                                        <Spinner animation="border" style={{ color: "#8B4513" }} />
                                        <p className="mt-3" style={{ color: "#8B4513" }}>
                                            Cargando feriados...
                                        </p>
                                    </div>
                                ) : feriados.length === 0 ? (
                                    <Alert variant="info">
                                        No hay feriados registrados para el año {filtroAño}
                                    </Alert>
                                ) : (
                                    <div className="table-responsive">
                                        <Table hover>
                                            <thead className="bg-light">
                                                <tr>
                                                    <th>Fecha</th>
                                                    <th>Nombre</th>
                                                    <th>Tipo</th>
                                                    <th>Región</th>
                                                    <th>Estado</th>
                                                    <th>Acciones</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {feriados.map(feriado => (
                                                    <tr key={feriado.id}>
                                                        <td>{formatearFecha(feriado.fecha)}</td>
                                                        <td className="fw-bold">{feriado.nombre}</td>
                                                        <td>
                                                            <Badge bg={getTipoBadge(feriado.tipo)}>
                                                                {feriado.tipo.toUpperCase()}
                                                            </Badge>
                                                        </td>
                                                        <td>{feriado.region || "-"}</td>
                                                        <td>
                                                            <Badge bg={feriado.activo ? "success" : "secondary"}>
                                                                {feriado.activo ? "ACTIVO" : "INACTIVO"}
                                                            </Badge>
                                                        </td>
                                                        <td>
                                                            <Button
                                                                variant="outline-warning"
                                                                size="sm"
                                                                className="me-2"
                                                                onClick={() => handleEditar(feriado)}
                                                            >
                                                                <FaEdit />
                                                            </Button>
                                                            <Button
                                                                variant="outline-danger"
                                                                size="sm"
                                                                onClick={() => handleEliminar(feriado)}
                                                            >
                                                                <FaTrash />
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Card.Body>
                </Card>

                {/* Modal */}
                <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>
                            {modoEdicion ? "Editar Feriado" : "Nuevo Feriado"}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Fecha *</Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={formData.fecha}
                                            onChange={(e) => setFormData({ ...formData, fecha: e.target.value })}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Tipo *</Form.Label>
                                        <Form.Select
                                            value={formData.tipo}
                                            onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                                        >
                                            <option value="nacional">Nacional</option>
                                            <option value="regional">Regional</option>
                                            <option value="empresa">Empresa</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Form.Group className="mb-3">
                                <Form.Label>Nombre *</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Ej: Año Nuevo, Día del Trabajo"
                                    value={formData.nombre}
                                    onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                                />
                            </Form.Group>
                            {formData.tipo === "regional" && (
                                <Form.Group className="mb-3">
                                    <Form.Label>Región</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Ej: CUSCO, AREQUIPA"
                                        value={formData.region}
                                        onChange={(e) => setFormData({ ...formData, region: e.target.value.toUpperCase() })}
                                    />
                                </Form.Group>
                            )}
                            <Form.Group className="mb-3">
                                <Form.Check
                                    type="checkbox"
                                    label="Activo"
                                    checked={formData.activo}
                                    onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
                                />
                            </Form.Group>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowModal(false)}>
                            Cancelar
                        </Button>
                        <Button variant="primary" onClick={handleGuardar}>
                            {modoEdicion ? "Actualizar" : "Guardar"}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </Container>
        </div>
    );
};

export default GestionFeriados;
